import { useState, useEffect, useRef } from "react";
import { DocFileType, TYPE_ICON, TYPE_COLOR } from "@/mocks/documentsData";

// ── File Type Icon ─────────────────────────────────────────────────────────────
export function FileTypeIcon({ type, size = "md" }: { type: DocFileType; size?: "sm" | "md" }) {
  const dim = size === "sm" ? "w-7 h-7" : "w-9 h-9";
  const iconSize = size === "sm" ? "text-xs" : "text-sm";
  return (
    <span
      className={`${dim} flex items-center justify-center rounded-lg shrink-0`}
      style={{ backgroundColor: `${TYPE_COLOR[type]}18` }}
    >
      <i className={`${TYPE_ICON[type]} ${iconSize}`} style={{ color: TYPE_COLOR[type] }}></i>
    </span>
  );
}

// ── Filter Dropdown ────────────────────────────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  active: boolean;
  closeOnSelect?: boolean;
  children: React.ReactNode;
}

export function FilterDropdown({
  label,
  active,
  closeOnSelect = false,
  children,
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border cursor-pointer transition-colors whitespace-nowrap ${
          active
            ? "border-[#830051] bg-[#830051]/5 text-[#830051]"
            : "border-gray-200 text-gray-500 hover:border-gray-300 bg-white"
        }`}
      >
        {label}
        <i className={open ? "ri-arrow-up-s-line text-xs" : "ri-arrow-down-s-line text-xs"}></i>
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-gray-100 rounded-xl z-50 min-w-[200px] py-1 max-h-72 overflow-y-auto"
          style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}
          onClick={() => {
            if (closeOnSelect) setOpen(false);
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ── Dropdown Option ────────────────────────────────────────────────────────────
export function DropdownOption({
  label,
  active,
  onClick,
  icon,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  icon?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-1.5 text-xs cursor-pointer hover:bg-gray-50 flex items-center gap-2 ${
        active ? "font-semibold text-[#830051]" : "text-gray-600"
      }`}
    >
      {icon && <i className={`${icon} shrink-0 text-gray-400`}></i>}
      <span className="truncate">{label}</span>
    </button>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <i className={`${icon} text-xl text-gray-400`}></i>
      </div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="text-xs text-gray-400 mt-1">{desc}</p>
    </div>
  );
}

// ── Permission Empty State (for no-access scenarios) ───────────────────────────
export function PermissionEmptyState({
  icon,
  title,
  desc,
}: {
  icon: string;
  title: string;
  desc: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
        style={{ backgroundColor: "rgba(131,0,81,0.06)" }}
      >
        <i className={`${icon} text-2xl`} style={{ color: "#830051" }}></i>
      </div>
      <p className="text-base font-semibold text-gray-700 mb-1">{title}</p>
      <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
    </div>
  );
}

// ── Upload Modal ───────────────────────────────────────────────────────────────
interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  isZh: boolean;
  studies: { code: string; name: string }[];
}

export function UploadModal({ isOpen, onClose, isZh, studies }: UploadModalProps) {
  const [dragOver, setDragOver] = useState(false);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-800">
              {isZh ? "上传热文档" : "Upload Document"}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {isZh
                ? "上传后文件将作为 MVR 报告的知识来源"
                : "Uploaded files will be used as knowledge sources for MVR reports"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
              dragOver
                ? "border-[#830051]/50 bg-[#830051]/3"
                : "border-gray-200 hover:border-[#830051]/30"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={() => setDragOver(false)}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: "rgba(131,0,81,0.06)" }}
            >
              <i className="ri-upload-cloud-2-line text-xl" style={{ color: "#830051" }}></i>
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              {isZh ? "拖拽文件到此处，或点击选择" : "Drag files here, or click to select"}
            </p>
            <p className="text-xs text-gray-400">
              {isZh
                ? "支持 PDF、Excel、Word、图片等格式，单个文件不超过 50MB"
                : "Supports PDF, Excel, Word, images, up to 50MB each"}
            </p>
          </div>

          {/* Study + Site */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">
                {isZh ? "关联研究" : "Study"}
              </label>
              <select className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#830051] cursor-pointer bg-white">
                {studies.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">
                {isZh ? "Site" : "Site"}
              </label>
              <input
                type="text"
                placeholder="e.g. 1302"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#830051] bg-white placeholder-gray-300"
              />
            </div>
          </div>

          {/* Patient + Visit */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">
                {isZh ? "患者" : "Patient"}
              </label>
              <input
                type="text"
                placeholder="e.g. 1302-001"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#830051] bg-white placeholder-gray-300"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">
                {isZh ? "访视" : "Visit"}
              </label>
              <input
                type="text"
                placeholder="e.g. Visit-5th"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#830051] bg-white placeholder-gray-300"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">
              {isZh ? "描述（选填）" : "Description (optional)"}
            </label>
            <textarea
              rows={2}
              placeholder={isZh ? "简要描述文件内容..." : "Brief description of this file..."}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none outline-none focus:border-[#830051] placeholder-gray-300"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors border border-gray-200 whitespace-nowrap"
          >
            {isZh ? "取消" : "Cancel"}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg cursor-pointer hover:opacity-90 transition-colors whitespace-nowrap"
            style={{ backgroundColor: "#830051" }}
          >
            {isZh ? "上传" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
